import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import { useAuthStore } from '@/stores/useAuthStore';

export interface EventItem {
  id: string;
  name: string;
  description: string;
  startDate: string;
  endDate: string;
  location: string;
  checksUsed: number;
  checksLimit: number;
  slug?: string;
  isActive: boolean;
  /** Offline practice event: no Atlas lead, no HubSpot record, no email out. */
  testMode: boolean;
  createdAt: string;
}

export function useEvents() {
  return useQuery({
    queryKey: ['events'],
    queryFn: () => apiFetch<{ events: EventItem[] }>('/api/events').then((d) => d.events),
    staleTime: 30_000,
  });
}

export function useCreateEvent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      name: string;
      startDate: string;
      endDate: string;
      location?: string;
      description?: string;
      checksLimit?: number;
      testMode?: boolean;
    }) =>
      apiFetch<EventItem>('/api/events', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
    },
  });
}

export function useUpdateEvent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      eventId,
      ...data
    }: {
      eventId: string;
      name?: string;
      startDate?: string;
      endDate?: string;
      location?: string;
      description?: string;
      checksLimit?: number;
      isActive?: boolean;
      testMode?: boolean;
    }) =>
      apiFetch<EventItem>(`/api/events/${eventId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
    },
  });
}

export function useEventStats(eventId: string | null) {
  return useQuery({
    queryKey: ['eventStats', eventId],
    queryFn: () => apiFetch<{
      totalLeads: number;
      todayLeads: number;
      hotLeads: number;
      atRiskLeads: number;
      avgScore: number;
      bandCounts: Record<string, number>;
      topCountries: { country: string; count: number }[];
      checksUsed: number;
      checksLimit: number;
    }>(`/api/events/${eventId}/stats`),
    enabled: !!eventId,
    staleTime: 30_000,
  });
}

export function useAutoSelectEvent() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const selectedEventId = useAuthStore((s) => s.selectedEventId);
  const setSelectedEventId = useAuthStore((s) => s.setSelectedEventId);

  useEffect(() => {
    if (!isAuthenticated || selectedEventId) return;

    const slug = localStorage.getItem('reevol-event-slug');
    if (!slug) return;

    apiFetch<{ events: EventItem[] }>('/api/events')
      .then(({ events }) => {
        const match = events.find((e) => e.slug === slug);
        if (match) {
          setSelectedEventId(match.id);
        } else if (events.length > 0) {
          // Slug not found (e.g. old account without this event) — select first event
          setSelectedEventId(events[0].id);
        }
        localStorage.removeItem('reevol-event-slug');
      })
      .catch(() => {
        localStorage.removeItem('reevol-event-slug');
      });
  }, [isAuthenticated, selectedEventId, setSelectedEventId]);
}
